import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Play, Heart, Trophy, Tv, Smartphone, Tablet, Monitor, Shield, Headphones, Check, Star, Quote, Gift } from 'lucide-react';
import api from '../services/api';
import PromoCampaignBanner from '../components/Promo/PromoCampaignBanner';

const Home = () => {
  const [reviews, setReviews] = useState([]);
  const [fromPrice, setFromPrice] = useState(null);
  const [fromMonthly, setFromMonthly] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get('/reviews/public');
        setReviews(response.data || []);
      } catch (err) {
        // Reviews not available
      }
    };
    fetchReviews();
    api.get('/pricing-matrix').then((r) => {
      const offers = r.data?.offers || [];
      if (offers.length) {
        const min = Math.min(...offers.map((o) => o.from_price));
        const best = offers.reduce((a, b) => (a.from_monthly < b.from_monthly ? a : b));
        setFromPrice(min);
        setFromMonthly(best.from_monthly);
      }
    }).catch(() => {});
  }, []);

  const fmtP = (n) => (Number.isInteger(Number(n)) ? String(n) : Number(n).toFixed(2)).replace('.', ',');
  const priceLabel = fromPrice != null ? `dès ${fmtP(fromPrice)}\u20AC` : '59\u20AC';
  const monthlyLabel = fromMonthly != null ? `soit dès ${fmtP(fromMonthly)}\u20AC/mois` : 'soit moins de 5\u20AC par mois';

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Helmet>
        <title>IPTV4KFR — Abonnement IPTV premium pour toute la famille (plusieurs durées)</title>
        <meta name="description" content="IPTV4KFR : plus de 15 000 chaînes en 4K, films et séries à la demande. Abonnement IPTV flexible, plusieurs durées et jusqu'à 5 connexions simultanées. Activation immédiate." />
      </Helmet>

      <PromoCampaignBanner />

      {/* HERO SECTION - Famille Premium */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1714978444538-9097293e5b20)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/85 via-[#0A0A0B]/70 to-[#0A0A0B]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
              Tous vos moments de divertissement,<br />
              <span className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E] bg-clip-text text-transparent">
                réunis en un seul endroit
              </span>
            </h1>
            <h2 className="text-base sm:text-lg text-[#E8E8E8]/90 mb-8 max-w-2xl mx-auto">
              Films, séries, sport en direct. Simple. Sans contrainte. Pour toute la famille.
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/inscription" 
                className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold text-lg px-8 py-4 rounded-lg hover:shadow-2xl hover:shadow-[#FF6B35]/50 transition-all duration-300"
                data-testid="cta-subscribe-button"
              >
                <Play className="inline-block mr-2 h-5 w-5" />
                Commencer maintenant
              </Link>
              <Link 
                to="/offre" 
                className="bg-white/10 backdrop-blur-sm text-white font-semibold text-lg px-8 py-4 rounded-lg hover:bg-white/20 transition-all duration-300 border border-white/20"
                data-testid="cta-discover-button"
              >
                Découvrir l'offre
              </Link>
            </div>

            <Link
              to="/connexion?action=trial"
              className="inline-flex items-center gap-2 mt-5 bg-[#00FFFF]/10 border border-[#00FFFF]/40 text-[#00FFFF] text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#00FFFF]/20 transition-colors"
              data-testid="hero-trial-badge"
            >
              <Gift className="h-4 w-4" /> Essai gratuit 24h, sans engagement
            </Link>

            <p className="text-[#E8E8E8]/70 mt-4 text-sm">
              {fromPrice != null ? `À partir de ${fmtP(fromPrice)}\u20AC` : 'À partir de 59\u20AC'} • Plusieurs durées • Support 24/7
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION FAMILLE FIRST */}
      <section className="py-20 bg-[#0A0A0B]" data-testid="family-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="h-8 w-8 text-[#FF6B35]" />
                <span className="text-[#FF6B35] font-semibold text-sm uppercase tracking-wide">Pour la famille</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
                Des soirées familiales<br />inoubliables
              </h2>
              <p className="text-base sm:text-lg text-[#E8E8E8]/80 mb-8 leading-relaxed">
                Films pour tous les âges, dessins animés, séries captivantes. Rassemblez-vous autour de vos contenus préférés et créez des moments précieux ensemble.
              </p>
              
              <div className="space-y-4">
                {[
                  "Programmes adaptés à toute la famille",
                  "Contrôle parental disponible",
                  "Des milliers de films et séries à la demande",
                  "Dessins animés et contenus pour enfants"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]" />
                    <span className="text-[#E8E8E8]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <div 
                className="relative rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1665827491450-c6f329f2285c)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '500px'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION SPORT FIRST */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#121214]" data-testid="sport-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div 
                className="relative rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1758687125988-0bac46eb0d4a)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '500px'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 to-transparent" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <Trophy className="h-8 w-8 text-[#F7931E]" />
                <span className="text-[#F7931E] font-semibold text-sm uppercase tracking-wide">Passion Sport</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
                Vibrez ensemble<br />à chaque match
              </h2>
              <p className="text-base sm:text-lg text-[#E8E8E8]/80 mb-8 leading-relaxed">
                Football, rugby, tennis, formule 1… Tous les grands événements sportifs en direct. Partagez l'émotion, célébrez les victoires, vivez chaque instant intensément.
              </p>
              
              <div className="space-y-4">
                {[
                  "Sport en direct et en haute qualité",
                  "Replays disponibles à tout moment",
                  "Tous les championnats internationaux",
                  "Multi-angles et statistiques en temps réel"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#F7931E]" />
                    <span className="text-[#E8E8E8]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION TOUS VOS ÉCRANS */}
      <section className="py-20 bg-[#121214]" data-testid="devices-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Chez vous. En déplacement. Partout.</h2>
            <p className="text-base sm:text-lg text-[#E8E8E8]/80 max-w-2xl mx-auto">
              Profitez de vos contenus préférés sur tous vos appareils. Commencez sur votre télé, continuez sur votre smartphone.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {[
              { icon: <Tv className="h-10 w-10" />, name: "Smart TV", desc: "Salon" },
              { icon: <Smartphone className="h-10 w-10" />, name: "Smartphone", desc: "En déplacement" },
              { icon: <Tablet className="h-10 w-10" />, name: "Tablette", desc: "Confort" },
              { icon: <Monitor className="h-10 w-10" />, name: "Ordinateur", desc: "Bureau" }
            ].map((device, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10 hover:border-[#FF6B35]/50 transition-all duration-300 text-center"
              >
                <div className="text-[#FF6B35] mb-4 flex justify-center">{device.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-1">{device.name}</h3>
                <p className="text-sm text-[#E8E8E8]/60">{device.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl max-w-4xl mx-auto"
          >
            <img 
              src="https://images.pexels.com/photos/29942709/pexels-photo-29942709.jpeg"
              alt="Streaming multi-devices"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/80 via-transparent to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* SECTION OFFRE SIMPLE */}
      <section className="py-20 bg-gradient-to-b from-[#121214] to-[#0A0A0B]" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Des offres flexibles, à votre rythme.</h2>
            <p className="text-base sm:text-lg text-[#E8E8E8]/80 mb-12 max-w-2xl mx-auto">
              Plusieurs durées et jusqu'à 5 connexions simultanées. Plus la durée est longue, plus le prix mensuel diminue.
            </p>
            
            <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-12 rounded-2xl border border-white/10 shadow-2xl">
              <div className="mb-8">
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-6xl sm:text-7xl font-bold bg-gradient-to-r from-[#FF6B35] to-[#F7931E] bg-clip-text text-transparent">{priceLabel}</span>
                </div>
                <p className="text-[#E8E8E8]/60">{monthlyLabel}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10 text-left max-w-2xl mx-auto">
                {[
                  "Plus de 10 000 chaînes en direct",
                  "Films et séries à la demande",
                  "Sport en direct et replays",
                  "Compatible tous appareils",
                  "Qualité 4K disponible",
                  "Support technique 24/7",
                  "Sans engagement",
                  "Mises à jour automatiques"
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="h-5 w-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                    <span className="text-[#E8E8E8]">{item}</span>
                  </div>
                ))}
              </div>

              <Link 
                to="/inscription" 
                className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold text-lg px-10 py-4 rounded-lg hover:shadow-2xl hover:shadow-[#FF6B35]/50 transition-all duration-300 inline-block"
                data-testid="pricing-cta-button"
              >
                Souscrire maintenant
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION RÉASSURANCE */}
      <section className="py-20 bg-[#0A0A0B]" data-testid="reassurance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Pourquoi nous faire confiance ?</h2>
            <p className="text-base sm:text-lg text-[#E8E8E8]/80">Une plateforme sérieuse et professionnelle à votre service</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="h-12 w-12 text-[#FF6B35]" />,
                title: "Paiement sécurisé",
                description: "Transactions protégées via Stripe. Vos données bancaires ne sont jamais stockées."
              },
              {
                icon: <Headphones className="h-12 w-12 text-[#FF6B35]" />,
                title: "Support réactif",
                description: "Une équipe disponible 24/7 pour répondre à toutes vos questions rapidement."
              },
              {
                icon: <Check className="h-12 w-12 text-[#FF6B35]" />,
                title: "Sans engagement",
                description: "Résiliez à tout moment. Pas de frais cachés, pas de piège. Total transparence."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10 hover:border-[#FF6B35]/50 transition-all duration-300 text-center"
              >
                <div className="mb-6 flex justify-center">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-[#E8E8E8]/70 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION AVIS CLIENTS */}
      {reviews.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#121214]" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Star className="h-6 w-6 text-[#FFB800] fill-[#FFB800]" />
                <span className="text-[#FFB800] font-semibold text-sm uppercase tracking-wide">Avis clients</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Ce que nos clients disent</h2>
              <p className="text-base sm:text-lg text-[#E8E8E8]/80 max-w-2xl mx-auto">
                Des milliers de familles nous font confiance au quotidien
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-[#1A1A1D] p-6 rounded-xl border border-white/10 hover:border-[#FF6B35]/30 transition-all duration-300"
                  data-testid={`review-card-${index}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${i < review.rating ? 'text-[#FFB800] fill-[#FFB800]' : 'text-[#3A3A3D]'}`}
                        />
                      ))}
                    </div>
                    <Quote className="h-5 w-5 text-[#FF6B35]/40" />
                  </div>
                  <p className="text-[#E8E8E8]/90 text-sm leading-relaxed mb-4">{review.comment}</p>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] flex items-center justify-center text-white text-sm font-bold">
                      {review.user_name?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <span className="text-[#B8B8BA] text-sm font-medium">{review.user_name}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#121214]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">Prêt à commencer ?</h2>
            <p className="text-base sm:text-lg text-[#E8E8E8]/80 mb-8 max-w-2xl mx-auto">
              Rejoignez des milliers de familles qui profitent déjà de IPTV4KFR.
            </p>
            
            <Link 
              to="/inscription" 
              className="bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold text-lg px-12 py-5 rounded-lg hover:shadow-2xl hover:shadow-[#FF6B35]/50 transition-all duration-300 inline-block"
            >
              Souscrire maintenant
            </Link>
            
            <p className="text-[#E8E8E8]/60 mt-6 text-sm">
              Installation en 5 minutes • Support inclus • Satisfait ou remboursé
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION VENDEUR (Conservée de l'original) */}
      <section className="py-20 bg-[#0A0A0B] border-t border-white/5" data-testid="vendor-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Devenez Vendeur IPTV4KFR</h2>
              <p className="text-[#E8E8E8]/80 text-lg mb-6">
                Rejoignez notre programme vendeur et générez des revenus récurrents en recommandant notre service IPTV premium.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "Commissions jusqu'à 25€ par vente",
                  "Bonus sur les parrainages de vos clients",
                  "Outils de suivi et dashboard dédié"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]" />
                    <span className="text-[#E8E8E8]">{item}</span>
                  </div>
                ))}
              </div>

              <Link 
                to="/devenir-vendeur" 
                className="bg-white/10 backdrop-blur-sm text-white font-semibold px-8 py-3 rounded-lg hover:bg-white/20 transition-all duration-300 border border-white/20 inline-block"
                data-testid="vendor-cta-button"
              >
                En savoir plus
              </Link>
            </div>

            <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6">Paliers de Commission</h3>
              <div className="space-y-4">
                {[
                  { range: "0-9 ventes/mois", commission: "10€" },
                  { range: "10-19 ventes/mois", commission: "15€" },
                  { range: "20-39 ventes/mois", commission: "20€" },
                  { range: "40+ ventes/mois", commission: "25€" }
                ].map((tier, index) => (
                  <div key={index} className="flex justify-between items-center bg-[#222226] p-4 rounded-lg">
                    <span className="text-[#E8E8E8]/80">{tier.range}</span>
                    <span className="text-[#FF6B35] font-bold text-lg">{tier.commission}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;